import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ship, MapPin, Package, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../../config';

const ShippingCalculator = ({ vehicle }) => {
  const [rules, setRules] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedPort, setSelectedPort] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('RoRo');
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const vehicleM3 = vehicle?.freight_m3_unit || 0;
  const hasDimensions = vehicleM3 > 0;

  useEffect(() => {
    fetchRules();
  }, [vehicle?.id]);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/public/shipping-rules`);
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching shipping rules:', err);
    }
  };

  const activeRule = rules.find(r => r.country === selectedCountry);

  useEffect(() => {
    if (activeRule) {
      setSelectedPort(activeRule.ports?.[0] || '');
      setSelectedMethod(activeRule.shipping_methods?.[0] || 'RoRo');
      setResult(null);
      setError('');
    }
  }, [selectedCountry]);

  if (rules.length === 0 && !loading) return null;

  const formatPrice = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateShipping = async () => {
    if (!selectedCountry) {
      setError('Please select a destination country');
      return;
    }

    setCalculating(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(`${API}/public/calculate-shipping`, {
        vehicle_id: vehicle.id,
        country: selectedCountry,
        port: selectedPort || undefined,
        shipping_method: selectedMethod
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to calculate shipping cost');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Ship className="h-5 w-5 text-red-600" />
          <span>Shipping Calculator</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Country Selector */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Destination Country</label>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {rules.filter(r => r.is_active !== false).map(rule => (
                <SelectItem key={rule.id} value={rule.country}>
                  {rule.country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rule Details */}
        {activeRule && (
          <div className="p-3 bg-gray-50 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Destination:</span>
              <span className="font-semibold text-gray-900">{activeRule.country}</span>
            </div>

            {activeRule.inspection_charges > 0 && (
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Inspection:</span>
                <span className="font-semibold text-gray-900">${activeRule.inspection_charges}</span>
              </div>
            )}

            {/* Port Selector */}
            {activeRule.ports && activeRule.ports.length > 0 && (
              <div className="mt-2">
                <label className="text-xs text-gray-600 mb-1 block">Port</label>
                <Select value={selectedPort} onValueChange={setSelectedPort}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select port" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeRule.ports.map(port => (
                      <SelectItem key={port} value={port}>{port}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Shipping Method */}
            {activeRule.shipping_methods && activeRule.shipping_methods.length > 0 && (
              <div className="mt-2">
                <label className="text-xs text-gray-600 mb-1 block">Shipping Method</label>
                <div className="flex items-center gap-4">
                  {activeRule.shipping_methods.map(m => (
                    <label key={m} className="flex items-center space-x-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="shipping_method"
                        value={m}
                        checked={selectedMethod === m}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="accent-red-600"
                      />
                      <span className="text-sm font-medium text-gray-900">{m}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Calculate Button */}
        <Button
          onClick={calculateShipping}
          disabled={calculating || !selectedCountry}
          className="w-full bg-red-600 hover:bg-red-700"
        >
          {calculating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Ship className="h-4 w-4 mr-2" />
              Calculate Shipping Cost
            </>
          )}
        </Button>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-700">Estimated Shipping Cost</span>
              <span className="text-2xl font-bold text-red-600">{formatPrice(result.shipping_cost)}</span>
            </div>

            {result.breakdown && (
              <div className="pt-3 border-t border-gray-200 space-y-2">
                <p className="text-xs font-semibold text-gray-700 mb-2">Cost Breakdown:</p>
                {result.breakdown.dimensions_available ? (
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Freight</span>
                    <span>{formatPrice(result.breakdown.freight)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Freight (to be calculated at destination)</span>
                    <span className="italic">TBD</span>
                  </div>
                )}
                {result.breakdown.inspection_charges > 0 && (
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Inspection Charges</span>
                    <span>{formatPrice(result.breakdown.inspection_charges)}</span>
                  </div>
                )}
              </div>
            )}

            {result.total_vehicle_price > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Vehicle Price</span>
                  <span className="text-sm font-semibold text-gray-900">{formatPrice(result.vehicle_price)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-semibold text-gray-700">Shipping Cost</span>
                  <span className="text-sm font-semibold text-gray-900">{formatPrice(result.shipping_cost)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-300 mt-2">
                  <span className="text-base font-bold text-gray-900">Total Vehicle Price</span>
                  <span className="text-xl font-bold text-red-600">{formatPrice(result.total_vehicle_price)}</span>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 italic pt-2 border-t border-gray-200">
              * Estimate only. Final costs may vary based on actual shipping conditions, customs fees, and port charges.
            </p>
          </div>
        )}

        {!result && !error && selectedCountry && activeRule && (
          <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded-lg">
            <p className="font-semibold mb-1">How it works:</p>
            <ul className="list-disc list-inside space-y-1">
              {hasDimensions ? (
                <li>Freight: calculated based on this vehicle's dimensions</li>
              ) : (
                <li>Freight: calculated at destination based on actual dimensions</li>
              )}
              {activeRule.inspection_charges > 0 && <li>Inspection: ${activeRule.inspection_charges}</li>}
              <li>Customs duties and local taxes are not included</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ShippingCalculator;
